import React, { useState, useEffect } from "react";
import { t } from "utils/i18n";
import { Fa } from "composants/Icon";
import Modal from "composants/Modal/Modal";
import { Document } from "./TabDocument";
import { analyzeDocumentWithMistral } from "api/documentAnalysis";
import toaster from "composants/toaster/toaster";

/**
 * Convertit le texte Markdown en HTML basique
 */
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return "";
  let processed = markdown;
  let html = processed.replace(/## (.*?)(\n|$)/g, '<h2 class="text-xl font-bold my-2">$1</h2>');

  // Gras (pour les en-têtes de sections comme "Résumé:")
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="block mt-2 mb-1">$1</strong>');

  // Listes à puces - espacement réduit et marge après la liste réduite
  html = html.replace(/^- (.*?)$/gm, '<li class="ml-4 py-0.5">$1</li>');
  html = html.replace(/(<li.*?>.*?<\/li>(\n|$))+/g, '<ul class="my-1 mb-2">$&</ul>');

  // Moins d'espace entre les paragraphes
  html = html.replace(/\n\n/g, '</p><p class="my-1">');

  // Traitement spécial pour la conclusion (souvent introduite par "Résumé:")
  html = html.replace(
    /<strong class="block mt-2 mb-1">Résumé:<\/strong>/g,
    '<strong class="block mt-2 mb-1 text-primary">Résumé:</strong>'
  );

  // Wrapper final avec style compact et meilleur espacement
  html = `<div class="markdown leading-tight text-sm space-y-1"><p class="my-1">${html}</p></div>`;

  // Nettoyage
  html = html.replace(/<p class="my-1"><ul/g, "<ul");
  html = html.replace(/<\/ul><\/p>/g, "</ul>");
  html = html.replace(/<p class="my-1"><h2/g, "<h2");
  html = html.replace(/<\/h2><\/p>/g, "</h2>");

  return html;
};

/**
 * Composant pour analyser un document avec l'IA
 */
const DocumentAiAnalysis: React.FC<{
  document: Document;
  onClose: () => void;
}> = ({ document, onClose }) => {
  const [analysis, setAnalysis] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const analyzeDocument = async () => {
      try {
        setLoading(true);

        // Utilisation de l'analyse directe avec Mistral
        const result = await analyzeDocumentWithMistral(document);
        setAnalysis(result.analysis);
        setLoading(false);
      } catch (error) {
        console.error("Erreur d'analyse:", error);
        setError("Une erreur est survenue lors de l'analyse du document.");
        toaster.error(t("commun_erreur_analyse_document"));
        setLoading(false);
      }
    };

    analyzeDocument();
  }, [document]);

  return (
    <Modal show={true} title={t("commun_analyse_ia_document")} onClose={onClose}>
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <Fa icon="spinner" spin size="3x" className="text-primary mb-4" />
            <div className="text-center">
              <p className="text-gray-600 mb-2">{t("commun_analyse_en_cours")}</p>
              <p className="text-xs text-gray-500">{t("commun_analyse_peut_prendre_temps")}</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-danger-light text-danger rounded">
            <Fa icon="exclamation-triangle" className="mr-2" />
            {error}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{t("commun_resume_document")}</h3>
              <div className="text-sm text-gray-500">
                <Fa icon="file" className="mr-1" />
                {document.nom}
              </div>
            </div>
            <div
              className="p-4 border rounded bg-light overflow-auto max-h-[70vh]"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(analysis) }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocumentAiAnalysis;
